// =========================================================================
// Loops section landing — projection only. Fork-owned (zone C), P5b lane.
//
// Port of the demo's `SECPAGE.loops` block (`renderSectionPage`, source line
// 3364): a hero band with four live figures, then one tile per loop carrying
// the loop's first sentence and its item count.
//
// Transformation layer (layer 3): pure functions over the loop seed and the
// engine. No React, no fetching, no mutation.
//
// @see docs/logic/loops/section.md
// =========================================================================

import { EXCHANGE, FLEET_RULES } from '../../shared/oem-engine';
import { LOOPS, type OemLoop } from '../../shared/seed/loops';

/** One figure in the section hero — demo `stats()`. */
export interface LoopSectionStat {
  /** Uppercase eyebrow, verbatim from the demo. */
  readonly eyebrow: string;
  /** The figure, already formatted. */
  readonly value: string;
  /** The line under it, verbatim. */
  readonly detail: string;
}

/**
 * The four hero figures.
 *
 * `bulletins` is the only one that is not compiled in: it comes from
 * `useOemData('parts')`, so the landing renders `—` until that file lands
 * rather than guessing at 13.
 */
export function loopSectionStats(bulletins: number | null): LoopSectionStat[] {
  return [
    { eyebrow: 'Loops', value: String(LOOPS.length), detail: 'feeding the modules' },
    {
      eyebrow: 'Fleet rules live',
      value: String(FLEET_RULES.length),
      detail: 'each came out of one job',
    },
    {
      eyebrow: 'Exchange units',
      value: String(EXCHANGE.length),
      detail: 'in play this fortnight',
    },
    {
      eyebrow: 'Bulletins',
      value: bulletins === null ? '—' : String(bulletins),
      detail: 'in force',
    },
  ];
}

/** One loop tile on the landing — demo `tiles()`. */
export interface LoopSectionTile {
  readonly id: string;
  readonly n: number;
  readonly name: string;
  /** Owning agent, printed under the name. */
  readonly agent: string;
  /** First sentence of the purpose — the demo's `sent1x`. */
  readonly blurb: string;
  /** Item-row count — the demo's tile figure, `l.items().length`. */
  readonly items: number;
  /** Loop swatch, carried on the seed as a hex literal. */
  readonly color: string;
  readonly to: string;
}

/**
 * The demo's `sent1x` (source line 3363): the first sentence of a paragraph,
 * trailing punctuation normalised back to a single full stop.
 */
export const firstSentence = (text: string): string =>
  `${text.split('. ')[0].replace(/[.\s]+$/, '')}.`;

/** The five loop tiles, in demo order. */
export const loopSectionTiles = (): LoopSectionTile[] =>
  LOOPS.map((loop: OemLoop) => ({
    id: loop.id,
    n: loop.n,
    name: loop.name,
    agent: loop.agent,
    blurb: firstSentence(loop.purpose),
    items: loop.items.length,
    color: loop.col,
    to: `/loops/${loop.id}`,
  }));
